import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  copyCustomView,
  createCustomViewShare,
  deleteCustomViewShare,
  fetchCustomViewShares,
  updateCustomViewShare,
} from '@/api/customViewsApi'
import { reloadCustomViews } from '@/services/customViewsSync'
import type { CreateCustomViewShareRequest } from '@/types/api'

const sharesKey = (viewId: number) => ['custom-view-shares', viewId] as const

export function useCustomViewShares(viewId: number, enabled: boolean) {
  return useQuery({
    queryKey: sharesKey(viewId),
    queryFn: () => fetchCustomViewShares(viewId),
    enabled,
  })
}

function useShareMutationSuccess(viewId: number) {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: sharesKey(viewId) }),
      reloadCustomViews(),
    ])
  }
}

export function useCreateCustomViewShare(viewId: number) {
  const onSuccess = useShareMutationSuccess(viewId)
  return useMutation({
    mutationFn: (request: CreateCustomViewShareRequest) => createCustomViewShare(viewId, request),
    onSuccess,
  })
}

export function useUpdateCustomViewShare(viewId: number) {
  const onSuccess = useShareMutationSuccess(viewId)
  return useMutation({
    mutationFn: ({ shareId, canEdit }: { shareId: number; canEdit: boolean }) =>
      updateCustomViewShare(viewId, shareId, { canEdit }),
    onSuccess,
  })
}

export function useDeleteCustomViewShare(viewId: number) {
  const onSuccess = useShareMutationSuccess(viewId)
  return useMutation({
    mutationFn: (shareId: number) => deleteCustomViewShare(viewId, shareId),
    onSuccess,
  })
}

export function useCopyCustomView(viewId: number) {
  return useMutation({
    mutationFn: (username: string) => copyCustomView(viewId, { username }),
  })
}
